import { useState } from "react";
import { Wrench, Search, PackageSearch, Loader2 } from "lucide-react";
import { api, ApiClientError } from "../../lib/api";
import { Stepper, type StepperStage } from "../../components/Stepper";

type PortalTab = "order" | "repair";

interface TrackOrderFound {
  found: true;
  order: {
    orderNumber: string;
    orderType: string;
    orderDate: string;
    value: number;
    status: string;
    fulfillmentStatus: string;
    paymentMethod: string;
  };
  customerName: string;
  stages: StepperStage[];
}
type TrackOrderResult = TrackOrderFound | { found: false };

interface TrackRepairFound {
  found: true;
  repairOrder: {
    id: string;
    equipment: string;
    modelNumber: string;
    serialNumber: string;
    serviceType: string;
    status: string;
    promisedDate: string;
    deliveryMethod: string;
  };
  customerName: string;
  stages: StepperStage[];
}
type TrackRepairResult = TrackRepairFound | { found: false };

function LookupForm({
  numberLabel,
  numberPlaceholder,
  busy,
  onSubmit,
}: {
  numberLabel: string;
  numberPlaceholder: string;
  busy: boolean;
  onSubmit: (number: string, contact: string) => void;
}) {
  const [number, setNumber] = useState("");
  const [contact, setContact] = useState("");

  return (
    <form
      className="flex flex-col gap-3.5"
      onSubmit={(e) => {
        e.preventDefault();
        if (!number.trim() || !contact.trim()) return;
        onSubmit(number.trim(), contact.trim());
      }}
    >
      <div>
        <label className="field-label">{numberLabel}</label>
        <input className="input" value={number} onChange={(e) => setNumber(e.target.value)} placeholder={numberPlaceholder} />
      </div>
      <div>
        <label className="field-label">Email or Account Number</label>
        <input
          className="input"
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          placeholder="For verification — the email or customer number on file"
        />
      </div>
      <button type="submit" className="btn-primary justify-center" disabled={busy || !number.trim() || !contact.trim()}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
        {busy ? "Looking up…" : "Track"}
      </button>
    </form>
  );
}

export function CustomerPortal() {
  const [tab, setTab] = useState<PortalTab>("order");

  const [orderResult, setOrderResult] = useState<TrackOrderResult | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [orderBusy, setOrderBusy] = useState(false);

  const [repairResult, setRepairResult] = useState<TrackRepairResult | null>(null);
  const [repairError, setRepairError] = useState<string | null>(null);
  const [repairBusy, setRepairBusy] = useState(false);

  async function trackOrder(number: string, contact: string) {
    setOrderBusy(true);
    setOrderError(null);
    setOrderResult(null);
    try {
      const result = await api.get<TrackOrderResult>(`/track/order?number=${encodeURIComponent(number)}&contact=${encodeURIComponent(contact)}`);
      setOrderResult(result);
    } catch (err) {
      setOrderError(err instanceof ApiClientError ? err.message : "Unable to look up this order right now. Please try again.");
    } finally {
      setOrderBusy(false);
    }
  }

  async function trackRepair(number: string, contact: string) {
    setRepairBusy(true);
    setRepairError(null);
    setRepairResult(null);
    try {
      const result = await api.get<TrackRepairResult>(`/track/repair?number=${encodeURIComponent(number)}&contact=${encodeURIComponent(contact)}`);
      setRepairResult(result);
    } catch (err) {
      setRepairError(err instanceof ApiClientError ? err.message : "Unable to look up this repair right now. Please try again.");
    } finally {
      setRepairBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface">
      <header className="bg-brand-navy-deep text-white px-4 py-5">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
            <Wrench size={20} className="text-brand-yellow" />
          </div>
          <div>
            <div className="font-extrabold text-base tracking-wide uppercase">SBD Service Center</div>
            <div className="text-slate-400 text-xs">Track your order or repair</div>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-8">
        <div className="flex items-center gap-1.5 bg-card border border-border rounded-md p-1 mb-6 w-fit mx-auto">
          <button
            className={`px-4 py-2 rounded text-sm font-bold transition-colors ${tab === "order" ? "bg-brand-blue text-white" : "text-ink-muted hover:text-ink"}`}
            onClick={() => setTab("order")}
          >
            Track My Order
          </button>
          <button
            className={`px-4 py-2 rounded text-sm font-bold transition-colors ${tab === "repair" ? "bg-brand-blue text-white" : "text-ink-muted hover:text-ink"}`}
            onClick={() => setTab("repair")}
          >
            Track My Repair
          </button>
        </div>

        {tab === "order" && (
          <div className="flex flex-col gap-5">
            <div className="card p-6">
              <h1 className="text-xl font-extrabold mb-1 text-center">Track My Order</h1>
              <p className="text-sm text-ink-muted text-center mb-5">Enter your order number and the email or account number on file.</p>
              <LookupForm numberLabel="Order Number" numberPlaceholder="e.g. R0123456" busy={orderBusy} onSubmit={(n, c) => void trackOrder(n, c)} />
            </div>

            {orderError && (
              <div className="card p-5 text-center text-status-red text-sm font-semibold" role="alert">
                {orderError}
              </div>
            )}

            {orderResult && !orderResult.found && (
              <div className="card p-6 text-center">
                <PackageSearch size={28} className="mx-auto mb-2.5 text-ink-muted" />
                <div className="font-bold mb-1">We couldn't find that order</div>
                <div className="text-sm text-ink-muted">
                  Please double-check the order number and the email or account number you entered, then try again.
                </div>
              </div>
            )}

            {orderResult && orderResult.found && (
              <div className="card p-6">
                <div className="text-center mb-5">
                  <div className="text-lg font-extrabold">{orderResult.order.orderNumber}</div>
                  <div className="text-sm text-ink-muted">{orderResult.customerName}</div>
                </div>
                <Stepper stages={orderResult.stages} />
                <div className="grid grid-cols-2 gap-3 text-sm mt-6 pt-5 border-t border-border">
                  <div>
                    <div className="text-ink-muted text-xs">Order Type</div>
                    <div className="font-semibold">{orderResult.order.orderType}</div>
                  </div>
                  <div>
                    <div className="text-ink-muted text-xs">Order Date</div>
                    <div className="font-semibold">{orderResult.order.orderDate.slice(0, 10)}</div>
                  </div>
                  <div>
                    <div className="text-ink-muted text-xs">Order Value</div>
                    <div className="font-semibold">${orderResult.order.value.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-ink-muted text-xs">Payment Method</div>
                    <div className="font-semibold">{orderResult.order.paymentMethod}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "repair" && (
          <div className="flex flex-col gap-5">
            <div className="card p-6">
              <h1 className="text-xl font-extrabold mb-1 text-center">Track My Repair</h1>
              <p className="text-sm text-ink-muted text-center mb-5">Enter your repair order number and the email or account number on file.</p>
              <LookupForm
                numberLabel="Repair Order Number"
                numberPlaceholder="e.g. RO123456"
                busy={repairBusy}
                onSubmit={(n, c) => void trackRepair(n, c)}
              />
            </div>

            {repairError && (
              <div className="card p-5 text-center text-status-red text-sm font-semibold" role="alert">
                {repairError}
              </div>
            )}

            {repairResult && !repairResult.found && (
              <div className="card p-6 text-center">
                <PackageSearch size={28} className="mx-auto mb-2.5 text-ink-muted" />
                <div className="font-bold mb-1">We couldn't find that repair order</div>
                <div className="text-sm text-ink-muted">
                  Please double-check the repair order number and the email or account number you entered, then try again.
                </div>
              </div>
            )}

            {repairResult && repairResult.found && (
              <div className="card p-6">
                <div className="text-center mb-5">
                  <div className="text-lg font-extrabold">{repairResult.repairOrder.id}</div>
                  <div className="text-sm text-ink-muted">{repairResult.customerName}</div>
                </div>
                <Stepper stages={repairResult.stages} />
                <div className="grid grid-cols-2 gap-3 text-sm mt-6 pt-5 border-t border-border">
                  <div>
                    <div className="text-ink-muted text-xs">Equipment</div>
                    <div className="font-semibold">
                      {repairResult.repairOrder.equipment} ({repairResult.repairOrder.modelNumber})
                    </div>
                  </div>
                  <div>
                    <div className="text-ink-muted text-xs">Serial #</div>
                    <div className="font-semibold">{repairResult.repairOrder.serialNumber}</div>
                  </div>
                  <div>
                    <div className="text-ink-muted text-xs">Promised Date</div>
                    <div className="font-semibold">{repairResult.repairOrder.promisedDate}</div>
                  </div>
                  <div>
                    <div className="text-ink-muted text-xs">Delivery Method</div>
                    <div className="font-semibold">{repairResult.repairOrder.deliveryMethod}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
